import { DialogContent } from '../../components/ui/DialogContent';
import { recordKey } from '../collection/multiCollectionIdentity';
import { MAX_COLLECTION_MOVE_ITEMS } from '@qkindex/contracts';
import { Alert, Button, Typography } from '@mui/material';
import BulkKeywordsModal from '../../components/BulkKeywordsModal';
import FindReplaceKeywordsModal from '../../components/FindReplaceKeywordsModal';
import { Combobox } from '../../components/ui/Combobox';
import {
  locationLabel,
  locationOptions,
} from '../locations/components/locationOptions';
import UpdateImageModal from '../../components/UpdateImageModal';
import { DialogActions } from '../../components/ui/DialogActions';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { Location } from '../../domain/location';

export type ItemBulkDialog =
  'keywords' | 'replace' | 'location' | 'image' | null;

export function ItemBulkModals({
  scopeKey,
  open,
  onClose,
  location,
  locations,
  onLocationChange,
  onLocationConfirm,
  onKeywordsConfirm,
  onReplaceConfirm,
  onImageCapture,
  imageProcessing,
  imageProgress,
  selectedCount = 0,
  locationBusy = false,
  locationsLoading = false,
  locationError,
}: {
  scopeKey: string;
  open: ItemBulkDialog;
  onClose: () => void;
  location: string;
  locations: Location[];
  onLocationChange: (value: string) => void;
  onLocationConfirm: () => void;
  onKeywordsConfirm: (keywords: string) => void;
  onReplaceConfirm: (find: RegExp, replacement: string) => void;
  onImageCapture: (file: File) => void;
  imageProcessing: boolean;
  imageProgress: number;
  selectedCount?: number;
  locationBusy?: boolean;
  locationsLoading?: boolean;
  locationError?: string;
}) {
  const closeLocation = () => {
    if (locationBusy) return;
    onLocationChange('');
    onClose();
  };
  const problem =
    selectedCount > MAX_COLLECTION_MOVE_ITEMS
      ? `Select at most ${MAX_COLLECTION_MOVE_ITEMS} items to move.`
      : location
        ? locationError
        : undefined;

  return (
    <>
      <ResponsiveDialog
        open={open === 'location'}
        onClose={closeLocation}
        aria-labelledby="bulk-location-title"
      >
        <DialogHeader
          id="bulk-location-title"
          title={
            selectedCount === 1 ? 'Move item' : `Move ${selectedCount} items`
          }
          onClose={closeLocation}
          closeDisabled={locationBusy}
        />
        <DialogContent>
          <Combobox<Location>
            label="Location"
            value={locations.find(
              (entry) =>
                recordKey(entry) === location ||
                (!entry.collectionId && entry.name === location),
            )}
            {...locationOptions(locations)}
            getOptionKey={recordKey}
            getOptionLabel={locationLabel}
            getOptionText={(entry) => entry.name}
            onChange={(entry) =>
              onLocationChange(
                entry.collectionId ? recordKey(entry) : entry.name,
              )
            }
            disabled={locationBusy}
          />
          {!locationsLoading && locations.length === 0 && (
            <Typography sx={{ mt: 1 }}>
              No available locations. Create a location before moving these
              items.
            </Typography>
          )}
          {problem && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {problem}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onLocationConfirm}
            variant="contained"
            disabled={
              locationBusy ||
              locationsLoading ||
              Boolean(locationError) ||
              selectedCount === 0 ||
              selectedCount > MAX_COLLECTION_MOVE_ITEMS ||
              !locations.some(
                (entry) =>
                  recordKey(entry) === location ||
                  (!entry.collectionId && entry.name === location),
              )
            }
          >
            {locationBusy ? 'Moving…' : 'Move Items'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
      <BulkKeywordsModal
        scopeKey={scopeKey}
        open={open === 'keywords'}
        onClose={onClose}
        onConfirm={onKeywordsConfirm}
      />
      <FindReplaceKeywordsModal
        scopeKey={scopeKey}
        open={open === 'replace'}
        onClose={onClose}
        onConfirm={onReplaceConfirm}
      />
      <UpdateImageModal
        open={open === 'image'}
        onClose={onClose}
        handleImageCapture={onImageCapture}
        compressionInProgress={imageProcessing}
        compressionProgress={imageProgress}
      />
    </>
  );
}
